import { useCallback, useEffect, useRef, useState } from 'react'

import { useOrulAuth, useTokens } from '@rhaimes/react'

import { api } from 'core/services'
import { orulAuthApi } from 'core/services/orul-auth-api'
import { orulTokenManager } from 'core/services/orul-token-manager'
import { PersistentTokenStorage } from 'core/services/orul-token-storage'
import {
  clearPendingSync,
  isTerminalSyncError,
  readPendingSync,
  savePendingSync,
} from 'core/services/phone-sync-queue'

import { mensagemDeErro } from './mensagem-de-erro'

export type PhoneChangeStep =
  | 'loading'
  | 'password'
  | 'phone'
  | 'code'
  | 'done'

export type PhoneResendMethod = 'sms' | 'voice-call'

export type UsePhoneChangeReturn = {
  step: PhoneChangeStep
  requiresPassword: boolean
  loading: boolean
  error: string | null
  destination: string | null
  syncWarning: string | null
  changeConfirmed: boolean
  resending: PhoneResendMethod | null
  resendCooldown: number
  resendsLeft: number | null
  resendNotice: string | null
  submitPassword: (password: string) => void
  submitPhone: (maskedPhone: string) => Promise<void>
  submitCode: (code: string) => Promise<void>
  resendCode: (method: PhoneResendMethod) => Promise<void>
}

const FALLBACK_ERROR = 'Não foi possível concluir. Tente novamente.'

const PHONE_FORMAT_ERROR =
  'Informe um número com DDD, no formato (00) 0 0000-0000.'

const PHONE_EM_USO_ERROR =
  'Esse número já está vinculado a outro cadastro. Use outro número ou procure o atendimento.'

const CHECAGEM_INDISPONIVEL_ERROR =
  'Não conseguimos verificar se esse número está disponível. Tente novamente em alguns minutos.'

const SYNC_INDETERMINADO_WARNING =
  'Não conseguimos confirmar se a troca foi concluída. Tente entrar com o número novo; se não funcionar, o número antigo continua valendo.'

const REENVIO_SMS_NOTICE = 'Enviamos um novo código por SMS.'

const REENVIO_LIGACAO_NOTICE =
  'Você vai receber uma ligação com o código. Atenda para ouvi-lo.'

/**
 * Espelha `RESEND_COOLDOWN_SECONDS` do orul-auth. Só vale quando a resposta não
 * traz `retryAfter` — o servidor é quem manda.
 */
const REENVIO_COOLDOWN_PADRAO = 60

const SYNC_RETRY_WARNING =
  'Seu login já usa o novo número. Estamos terminando de atualizar seu cadastro e vamos tentar de novo automaticamente.'

/**
 * Converte o telefone mascarado da tela para o formato do backend.
 *
 * O `+` e obrigatorio: `normalizePhoneNumber` no backend so prefixa o codigo do
 * pais quando o numero NAO comeca com `+`. Enviar sem o sinal produziria
 * `+5555...`, com o 55 duplicado.
 */
function toE164(maskedPhone: string): string {
  return `+55${maskedPhone.replace(/\D/g, '')}`
}

/**
 * A pendencia e de um usuario so. Em aparelho compartilhado (balcao), limpar
 * sem checar o dono apagaria a sincronizacao pendente de outra pessoa.
 */
function limparPendenciaDoUsuario(sub: string): void {
  if (readPendingSync()?.sub === sub) {
    clearPendingSync()
  }
}

export function usePhoneChange(): UsePhoneChangeReturn {
  const { refreshSession, user } = useOrulAuth()
  const { tokens } = useTokens()

  const passwordRef = useRef('')
  const requestIdRef = useRef('')

  const [step, setStep] = useState<PhoneChangeStep>('loading')
  // Default `true` de proposito: se a consulta falhar, o fluxo pede a senha.
  // O erro que custa caro aqui e dispensar a revalidacao de quem tem senha.
  const [requiresPassword, setRequiresPassword] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [destination, setDestination] = useState<string | null>(null)
  const [syncWarning, setSyncWarning] = useState<string | null>(null)
  const [changeConfirmed, setChangeConfirmed] = useState(false)
  const [resending, setResending] = useState<PhoneResendMethod | null>(null)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [resendsLeft, setResendsLeft] = useState<number | null>(null)
  const [resendNotice, setResendNotice] = useState<string | null>(null)

  // Conta federada (login por Google) nao tem senha para informar: pedi-la
  // devolvia "Senha incorreta" sobre uma senha que nunca existiu, e nao havia
  // nada que o usuario pudesse digitar para sair dali. Quem decide e o
  // servidor — o app so pergunta antes de desenhar o primeiro passo.
  useEffect(() => {
    let ativo = true

    orulAuthApi
      .get('/api/v1/auth/phone-change/requirements')
      .then(({ data }) => {
        if (!ativo) return

        const exigeSenha = data?.requiresPassword !== false

        setRequiresPassword(exigeSenha)
        // Só assume o passo quando a tela ainda está esperando: uma resposta
        // lenta que chegasse depois do primeiro avanço jogaria o usuário de
        // volta ao começo.
        setStep(atual =>
          atual === 'loading' ? (exigeSenha ? 'password' : 'phone') : atual,
        )
      })
      .catch(() => {
        if (!ativo) return

        setStep(atual => (atual === 'loading' ? 'password' : atual))
      })

    return () => {
      ativo = false
    }
  }, [])

  // Um setTimeout por segundo, reagendado pela propria mudanca do estado: o
  // setInterval sobreviveria a um desmonte no meio da contagem.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined

    const id = setTimeout(() => setResendCooldown(atual => atual - 1), 1000)

    return () => clearTimeout(id)
  }, [resendCooldown])

  const submitPassword = useCallback((password: string) => {
    passwordRef.current = password
    setError(null)
    setStep('phone')
  }, [])

  const submitPhone = useCallback(
    async (maskedPhone: string) => {
      if (loading) return

      // Defesa em profundidade: a mascara do modal ja impede o formato errado,
      // mas `toE164` so remove nao-digitos — um numero ja prefixado com 55
      // viraria `+5555...`. Alem disso, cada request gasta uma das 3
      // solicitacoes por hora e dispara um SMS.
      const digitos = maskedPhone.replace(/\D/g, '')

      if (digitos.length < 10 || digitos.length > 11) {
        setError(PHONE_FORMAT_ERROR)
        return
      }

      setLoading(true)
      setError(null)

      const novoTelefone = toE164(maskedPhone)

      // Checagem antes do SMS porque `orul-auth` e `toty` discordam sobre o que e um numero
      // ocupado: o `orul-auth` so consulta o Cognito, e um cadastro de balcao no toty nao tem
      // conta no Cognito. Sem isso a troca era confirmada por cima do cadastro de outra pessoa, e
      // o `phone-sync` so descobria depois do confirm — quando o Cognito ja tinha trocado e nao
      // tinha mais volta. Falhar aqui tambem economiza um SMS e uma das 3 solicitacoes por hora.
      //
      // Bloco proprio, e nao junto do request: se a CHECAGEM falhar (rede, 500, rota ausente) nao
      // da para seguir. Falha fechado — confirmar uma troca sem saber se o numero esta livre e o
      // erro que custa caro, e ele e irreversivel.
      try {
        const { data: disponibilidade } = await api.get(
          '/clients/me/phone-availability',
          { params: { phone: novoTelefone } },
        )

        if (disponibilidade?.available === false) {
          setError(PHONE_EM_USO_ERROR)
          setLoading(false)
          return
        }
      } catch {
        setError(CHECAGEM_INDISPONIVEL_ERROR)
        setLoading(false)
        return
      }

      try {
        const { data } = await orulAuthApi.post(
          '/api/v1/auth/phone-change/request',
          // `password` fora do corpo quando a conta nao tem senha: o schema
          // do backend recusa string vazia e responderia 400 de validacao.
          {
            newPhone: novoTelefone,
            ...(passwordRef.current ? { password: passwordRef.current } : {}),
          },
        )

        requestIdRef.current = data.requestId
        setDestination(data.destination)
        // O SMS acabou de sair: o servidor conta o intervalo a partir da
        // criacao da solicitacao, entao a tela comeca com os botoes em espera
        // em vez de oferecer um reenvio que voltaria 429.
        setResendCooldown(REENVIO_COOLDOWN_PADRAO)
        setResendsLeft(null)
        setResendNotice(null)
        setStep('code')
      } catch (err) {
        setError(mensagemDeErro(err))
      } finally {
        setLoading(false)
      }
    },
    [loading],
  )

  const submitCode = useCallback(
    async (code: string) => {
      if (loading) return

      setLoading(true)
      setError(null)

      // Capturado ANTES do confirm: depois do refreshSession este token deixa
      // de estar disponivel, e o phone-sync depende dele para provar que a
      // sessao antiga e a nova sao do mesmo usuario.
      //
      // Falha fechado: sem o token nao existe caminho de sincronizacao, e o
      // confirm e irreversivel. Abortar aqui e a unica saida sem custo.
      const previousIdToken = tokens?.idToken
      const sub = user?.id

      if (!previousIdToken || !sub) {
        setError(FALLBACK_ERROR)
        setLoading(false)
        return
      }

      try {
        await orulAuthApi.post('/api/v1/auth/phone-change/confirm', {
          requestId: requestIdRef.current,
          code,
        })
      } catch (err: any) {
        const status = err?.response?.status

        // So um 4xx e conclusivo — o backend respondeu e recusou. Sem resposta
        // (timeout, queda) ou com 5xx, a troca pode ter sido efetivada e a
        // resposta perdida. Tratar como falha limpa descartaria o
        // previousIdToken e deixaria o cadastro orfao sem rastro, entao o
        // caminho seguro e gravar a pendencia e concluir com aviso.
        if (typeof status === 'number' && status >= 400 && status < 500) {
          setError(mensagemDeErro(err))
          setLoading(false)
          return
        }

        // Reemite antes de gravar: sem isso a sessao fica com o token antigo,
        // o flush da proxima montagem sai com token antigo no header e no
        // corpo, o backend responde 400 "nada a sincronizar" — que e terminal —
        // e a pendencia morre em silencio. Se o confirm nao tiver acontecido,
        // reemitir aqui e inofensivo.
        try {
          await refreshSession()
        } catch {
          // Sem sessao nova a pendencia ainda vale: o flush da proxima
          // abertura encontra a sessao ja renovada pelo proprio SDK.
        }

        savePendingSync(previousIdToken, sub)
        setSyncWarning(SYNC_INDETERMINADO_WARNING)
        setStep('done')
        setLoading(false)
        return
      }

      // A partir daqui a troca de login ja aconteceu e nao tem volta. Nada
      // abaixo pode apresentar o fluxo como falha.
      setChangeConfirmed(true)

      try {
        await refreshSession()
      } catch {
        // Sem sessao nova, o phone-sync imediato iria com token velho no header
        // e seria recusado. Grava a pendencia e sai: o reenvio na proxima
        // abertura do app pega a sessao ja renovada.
        savePendingSync(previousIdToken, sub)
        setSyncWarning(SYNC_RETRY_WARNING)
        setStep('done')
        setLoading(false)
        return
      }

      // O orulTokenManager so e alimentado por um useEffect do provider, e a
      // continuacao deste await e microtask — o efeito ainda nao rodou. Sem
      // empurrar o token novo aqui, o phone-sync sairia com o token ANTIGO no
      // header; o backend o compararia com o previousIdToken identico e
      // responderia 400 "Nenhuma troca a sincronizar", em toda troca.
      const renovados = new PersistentTokenStorage().getTokens()

      if (renovados?.idToken) {
        orulTokenManager.setToken(renovados.idToken, renovados.expiresAt)
      }

      try {
        await api.post('/clients/me/phone-sync', { previousIdToken })
        limparPendenciaDoUsuario(sub)
      } catch (err: any) {
        if (isTerminalSyncError(err)) {
          // Definitivo: retry nao muda o resultado. So o 409 descreve algo que
          // o usuario pode levar ao atendimento; nos demais o backend ja
          // gravou a linha de reconciliacao e uma pendencia deste usuario
          // deixa de ter para que existir.
          if (err?.response?.status === 409) {
            setSyncWarning(mensagemDeErro(err))
          } else {
            limparPendenciaDoUsuario(sub)
          }
        } else {
          savePendingSync(previousIdToken, sub)
          setSyncWarning(SYNC_RETRY_WARNING)
        }
      }

      setStep('done')
      setLoading(false)
    },
    [loading, refreshSession, tokens?.idToken, user?.id],
  )

  const resendCode = useCallback(
    async (method: PhoneResendMethod) => {
      // O cooldown e o teto tambem sao checados no servidor; aqui a checagem
      // so evita a ida inutil e o piscar de erro na tela.
      if (loading || resending || resendCooldown > 0) return
      if (!requestIdRef.current) return

      setResending(method)
      setError(null)
      setResendNotice(null)

      try {
        const { data } = await orulAuthApi.post(
          '/api/v1/auth/phone-change/resend',
          { requestId: requestIdRef.current, method },
        )

        // O destino pode ter vindo mascarado de forma diferente; e o mesmo
        // numero, mas a tela mostra o que o servidor acabou de confirmar.
        if (data?.destination) setDestination(data.destination)

        setResendsLeft(
          typeof data?.resendsLeft === 'number' ? data.resendsLeft : null,
        )
        setResendCooldown(
          typeof data?.retryAfter === 'number'
            ? data.retryAfter
            : REENVIO_COOLDOWN_PADRAO,
        )
        setResendNotice(
          method === 'sms' ? REENVIO_SMS_NOTICE : REENVIO_LIGACAO_NOTICE,
        )
      } catch (err: any) {
        // 429 aqui e o proprio limite de reenvio, nao uma falha: segurar os
        // botoes pelo cooldown evita que o usuario insista contra uma porta
        // que o servidor ja fechou.
        if (err?.response?.status === 429) {
          setResendCooldown(REENVIO_COOLDOWN_PADRAO)
        }

        setError(mensagemDeErro(err))
      } finally {
        setResending(null)
      }
    },
    [loading, resending, resendCooldown],
  )

  return {
    step,
    requiresPassword,
    loading,
    error,
    destination,
    syncWarning,
    changeConfirmed,
    resending,
    resendCooldown,
    resendsLeft,
    resendNotice,
    submitPassword,
    submitPhone,
    submitCode,
    resendCode,
  }
}
